import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { IoChevronDown } from 'react-icons/io5'
import { SWRConfig } from 'swr'
import { Toaster } from 'react-hot-toast'
import type { FixtureLite, FixtureBuilderOut, FixturePlayersOut, TeamRollupOut, TeamSeasonStatsOut, HeadToHeadOut, H2HStatsResponse, H2HFilter, CardsContextResponse, TeamLite } from './types'
import {
  swrConfig,
  useFixtures,
  useFixtureLite,
  useFixtureBuilder,
  useFixtureTeams,
  useFixtureSeasonStats,
  useFixtureHeadToHead,
  useFixturePlayers,
  useH2HStats,
  useFixtureCardsContext,
} from './swr-config'
import { fmtKickoff, fmtDateHeader, getDateKey } from './utils'
import { apiGet } from './api'
import {
  FixtureCard,
  TabNav,
  BuilderTab,
  TeamsTab,
  PlayersTab,
  H2HTab,
  CardsTab,
  BottomNav,
  BetBuilderModal,
  BetBuilderPanel,
  SettingsModal,
  MyBetsPage,
  RoadmapPage,
  ValueBetsPage,
  LiveTipsPage,
  AdminPage,
  RefereesPage,
  RefereeDetailPage,
  AIPredictionsPage,
  PlayerStatsPage,
  CollapsibleSection,
  FixtureNotes,
  ErrorBoundary,
  Ticker,
  Sidebar,
  MobileHeader,
  InsightsSummary,
  SearchModal,
  CardsHub,
  GoalsHub,
  StrategiesPage,
  DixonColesPage,
  MatchPage,
  TeamPage,
  LeaguePage,
  DashboardPage,
  type Tab,
  type PageId,
} from './components'
import { BetProvider, useBetBuilder } from './context/BetContext'
import { UserDataProvider } from './context/UserDataContext'
import { ThemeProvider } from './context/ThemeContext'
import { LeagueProvider, useLeague, isLiveTodayMode } from './context/LeagueContext'
import { OddsProvider, useOdds } from './context/OddsContext'
import { LiveTipsProvider } from './context/LiveTipsContext'
import { SidebarProvider, useSidebar } from './context/SidebarContext'
import { NavigationProvider, useNavigationHandler, useNavigation } from './context/NavigationContext'
import { useAuthSetup, useLiveUpdates, useAlerts, type LiveUpdate } from './hooks'
import { useAnalyticsUserSync, FeatureNames } from './hooks/useActivityTracking'
import { trackFeatureEnter } from './utils/analytics'
import { PWAInstallBanner } from './components/PWAInstallBanner'

// Group fixtures by status
function groupFixturesByStatus(fixtures: FixtureLite[]) {
  const live: FixtureLite[] = []
  const completed: FixtureLite[] = []
  const upcoming: FixtureLite[] = []

  fixtures.forEach((f) => {
    if (f.status === 'LIVE') {
      live.push(f)
    } else if (f.status === 'FT') {
      completed.push(f)
    } else {
      upcoming.push(f)
    }
  })

  return { live, completed, upcoming }
}

function MainContent({
  fixtures,
  selected,
  setSelected,
  tab,
  setTab,
  builder,
  teams,
  seasonStats,
  headToHead,
  players,
  h2hStats,
  h2hFilter,
  onH2HFilterChange,
  cardsContext,
  err,
  fixtureLabel,
  activePage,
  onPageChange,
  onRefresh,
  isRefreshing,
  isLiveToday,
  onFixtureClick,
  onMatchBack,
  matchFixtureId,
  selectedTeam,
  pendingTeamId,
  onTeamClick,
  onTeamBack,
  selectedLeagueId,
  onLeagueClick,
  onLeagueBack,
}: {
  fixtures: FixtureLite[]
  selected: FixtureLite | null
  setSelected: (f: FixtureLite | null) => void
  tab: Tab
  setTab: (t: Tab) => void
  builder: FixtureBuilderOut | null
  teams: TeamRollupOut[] | null
  seasonStats: TeamSeasonStatsOut[] | null
  headToHead: HeadToHeadOut | null
  players: FixturePlayersOut | null
  h2hStats: H2HStatsResponse | null
  h2hFilter: H2HFilter
  onH2HFilterChange: (filter: H2HFilter) => void
  cardsContext: CardsContextResponse | null
  err: string | null
  fixtureLabel: string
  activePage: PageId
  onPageChange: (page: PageId) => void
  onRefresh: () => Promise<void>
  isRefreshing: boolean
  isLiveToday: boolean
  onFixtureClick: (fixture: FixtureLite) => void
  onMatchBack: () => void
  matchFixtureId: number | null
  selectedTeam: TeamLite | null
  pendingTeamId: number | null
  onTeamClick: (team: TeamLite) => void
  onTeamBack: () => void
  selectedLeagueId: number | null
  onLeagueClick: (apiLeagueId: number) => void
  onLeagueBack: () => void
}) {
  const { isModalOpen } = useBetBuilder()
  const { fetchMatchWinnerOdds, fetchFullFixtureOdds } = useOdds()
  const { scrollToFixtureId, clearScrollToFixture, scrollToRefereeId } = useNavigation()

  // Track expanded leagues in Live Today mode (all collapsed by default)
  const [expandedLeagues, setExpandedLeagues] = useState<Set<number>>(new Set())

  // Toggle league expansion
  const toggleLeague = (leagueId: number) => {
    setExpandedLeagues(prev => {
      const next = new Set(prev)
      if (next.has(leagueId)) {
        next.delete(leagueId)
      } else {
        next.add(leagueId)
      }
      return next
    })
  }

  // Referee page state - tracks which referee detail page to show (null = show list)
  const [selectedRefereeId, setSelectedRefereeId] = useState<number | null>(null)
  // Track referee to highlight in list (from search navigation, not for detail page)
  const [highlightRefereeId, setHighlightRefereeId] = useState<number | null>(null)

  // When scrollToRefereeId changes, update highlightRefereeId but keep showing the list
  useEffect(() => {
    if (scrollToRefereeId) {
      setHighlightRefereeId(scrollToRefereeId)
      // Make sure we're showing the list, not the detail page
      setSelectedRefereeId(null)
    }
  }, [scrollToRefereeId])

  // Reset referee selection when navigating away from referees page
  useEffect(() => {
    if (activePage !== 'referees') {
      setSelectedRefereeId(null)
      setHighlightRefereeId(null)
    }
  }, [activePage])

  // Navigate to referee detail page from match page
  const handleRefereeClick = (refereeId: number) => {
    setSelectedRefereeId(refereeId)
    onPageChange('referees')
  }

  // Group fixtures by status
  const { live, completed, upcoming } = useMemo(
    () => groupFixturesByStatus(fixtures),
    [fixtures]
  )

  // Batch fetch 1X2 odds for all upcoming fixtures on page load
  // Note: fetchMatchWinnerOdds excluded from deps to prevent infinite loop
  // (it updates oddsCache which recreates the function)
  useEffect(() => {
    if (upcoming.length > 0) {
      const upcomingIds = upcoming.map((f) => f.id)
      fetchMatchWinnerOdds(upcomingIds)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcoming])

  // Fetch full odds when a fixture is selected (for the builder)
  // Note: fetchFullFixtureOdds excluded from deps to prevent infinite loop
  useEffect(() => {
    if (selected) {
      fetchFullFixtureOdds(selected.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id])

  const handleFixtureToggle = (fixture: FixtureLite) => {
    // If clicking the same fixture, collapse it; otherwise expand the new one
    if (selected?.id === fixture.id) {
      setSelected(null)
    } else {
      setSelected(fixture)
    }
  }

  // Helper to render a single fixture
  const renderFixture = (f: FixtureLite) => {
    const shouldScroll = scrollToFixtureId === f.id

    // Clear scroll flag after rendering (will scroll on next render cycle)
    if (shouldScroll) {
      setTimeout(() => clearScrollToFixture(), 500)
    }

    return (
      <FixtureCard
        key={f.id}
        fixture={f}
        isSelected={false}
        onSelect={() => onFixtureClick(f)}
        scrollIntoView={shouldScroll}
      />
    )
  }

  // Helper to render a list of fixtures with date/league separators
  const renderFixtures = (fixtureList: FixtureLite[]) => {
    // In Live Today mode, group fixtures by league and render collapsible sections
    if (isLiveToday) {
      // Group fixtures by league
      const leagueGroups = new Map<number, { name: string; fixtures: FixtureLite[] }>()
      fixtureList.forEach((f) => {
        const leagueId = f.league_id ?? -999
        if (!leagueGroups.has(leagueId)) {
          leagueGroups.set(leagueId, { name: f.league_name || 'Unknown League', fixtures: [] })
        }
        leagueGroups.get(leagueId)!.fixtures.push(f)
      })

      return Array.from(leagueGroups.entries()).map(([leagueId, { name, fixtures: leagueFixtures }]) => {
        const isExpanded = expandedLeagues.has(leagueId)
        const fixtureCount = leagueFixtures.length

        return (
          <div key={`league-group-${leagueId}`} className="fixture-league-group">
            <button
              type="button"
              className={`fixture-league-header ${isExpanded ? 'expanded' : ''}`}
              onClick={() => toggleLeague(leagueId)}
            >
              <span className="fixture-league-name">{name}</span>
              <span className="fixture-league-count">{fixtureCount}</span>
              <IoChevronDown
                size={14}
                className={`fixture-league-chevron ${isExpanded ? 'expanded' : ''}`}
              />
            </button>
            {isExpanded && (
              <div className="fixture-league-content">
                {(() => {
                  const elements: React.ReactNode[] = []
                  let lastDateKey = ''

                  leagueFixtures.forEach((f) => {
                    const dateKey = getDateKey(f.kickoff_utc)

                    // Add date separator if this is a new date
                    if (dateKey !== lastDateKey) {
                      elements.push(
                        <div key={`date-${dateKey}-${leagueId}`} className="fixture-date-separator">
                          {fmtDateHeader(f.kickoff_utc)}
                        </div>
                      )
                      lastDateKey = dateKey
                    }

                    elements.push(renderFixture(f))
                  })

                  return elements
                })()}
              </div>
            )}
          </div>
        )
      })
    }

    // Normal mode: just date separators
    const elements: React.ReactNode[] = []
    let lastDateKey = ''

    fixtureList.forEach((f) => {
      const dateKey = getDateKey(f.kickoff_utc)

      // Add date separator if this is a new date
      if (dateKey !== lastDateKey) {
        elements.push(
          <div key={`date-${dateKey}`} className="fixture-date-separator">
            {fmtDateHeader(f.kickoff_utc)}
          </div>
        )
        lastDateKey = dateKey
      }

      elements.push(renderFixture(f))
    })

    return elements
  }

  const { isExpanded } = useSidebar()

  return (
    <div className="app-layout">
      {/* Mobile header */}
      <MobileHeader />

      {/* Sidebar */}
      <Sidebar
        activePage={activePage}
        onPageChange={onPageChange}
        onLeagueClick={onLeagueClick}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
      />

      {/* Main content area */}
      <main id="main-content" className={`app-main ${!isExpanded ? 'sidebar-collapsed' : ''}`} tabIndex={-1}>
        {/* CrypGo-style scrolling ticker for fixtures page */}
        {activePage === 'fixtures' && <Ticker fixture={selected} builder={builder} />}

        {activePage === 'dashboard' ? (
          <div className="container dashboard-container">
            <ErrorBoundary name="Dashboard">
              <DashboardPage onNavigate={onPageChange} onLeagueClick={onLeagueClick} />
            </ErrorBoundary>
          </div>
        ) : activePage === 'admin' ? (
          <div className="container admin-container">
            <ErrorBoundary name="Admin Page">
              <AdminPage />
            </ErrorBoundary>
          </div>
        ) : activePage === 'roadmap' ? (
          <div className="container roadmap-container">
            <ErrorBoundary name="Roadmap Page">
              <RoadmapPage />
            </ErrorBoundary>
          </div>
        ) : activePage === 'mybets' ? (
          <div className="container">
            <ErrorBoundary name="My Bets Page">
              <MyBetsPage fixtures={fixtures} />
            </ErrorBoundary>
          </div>
        ) : activePage === 'valuebets' ? (
          <div className={`container valuebets-container ${isModalOpen ? 'panel-open' : ''}`}>
            <div className={`grid valuebets ${isModalOpen ? 'with-panel' : ''}`}>
              <div className="valuebets-list-column">
                <ErrorBoundary name="Value Bets">
                  <ValueBetsPage />
                </ErrorBoundary>
              </div>
              <BetBuilderPanel />
            </div>
          </div>
        ) : activePage === 'livetips' ? (
          <div className="container">
            <ErrorBoundary name="Live Tips Page">
              <LiveTipsPage />
            </ErrorBoundary>
          </div>
        ) : activePage === 'referees' ? (
          <div className="container">
            <ErrorBoundary name="Referees Page">
              {selectedRefereeId ? (
                <RefereeDetailPage
                  refereeId={selectedRefereeId}
                  onBack={() => setSelectedRefereeId(null)}
                />
              ) : (
                <RefereesPage
                  onRefereeSelect={setSelectedRefereeId}
                  highlightRefereeId={highlightRefereeId}
                />
              )}
            </ErrorBoundary>
          </div>
        ) : activePage === 'aipredictions' ? (
          <div className="container">
            <ErrorBoundary name="AI Predictions Page">
              <AIPredictionsPage />
            </ErrorBoundary>
          </div>
        ) : activePage === 'playerstats' ? (
          <div className="container">
            <ErrorBoundary name="Player Stats">
              <PlayerStatsPage />
            </ErrorBoundary>
          </div>
        ) : activePage === 'cardshub' ? (
          <div className="container cards-hub-container">
            <ErrorBoundary name="Cards Hub">
              <CardsHub />
            </ErrorBoundary>
          </div>
        ) : activePage === 'goalshub' ? (
          <div className="container goals-hub-container">
            <ErrorBoundary name="Goals Hub">
              <GoalsHub />
            </ErrorBoundary>
          </div>
        ) : activePage === 'strategies' ? (
          <div className={`container strategies-container ${isModalOpen ? 'panel-open' : ''}`}>
            <div className={`grid strategies ${isModalOpen ? 'with-panel' : ''}`}>
              <div className="strategies-list-column">
                <ErrorBoundary name="Strategies">
                  <StrategiesPage />
                </ErrorBoundary>
              </div>
              <BetBuilderPanel />
            </div>
          </div>
        ) : activePage === 'dixoncoles' ? (
          <div className="container dixon-coles-container">
            <ErrorBoundary name="Dixon-Coles">
              <DixonColesPage />
            </ErrorBoundary>
          </div>
        ) : activePage === 'league' && selectedLeagueId ? (
          <div className="container league-container">
            <ErrorBoundary name="League Page">
              <LeaguePage
                apiLeagueId={selectedLeagueId}
                onBack={onLeagueBack}
                onFixtureClick={onFixtureClick}
                onTeamClick={onTeamClick}
              />
            </ErrorBoundary>
          </div>
        ) : activePage === 'match' && (selectedTeam || pendingTeamId) ? (
          <div className="container team-container">
            <ErrorBoundary name="Team Page">
              {selectedTeam ? (
                <TeamPage
                  team={selectedTeam}
                  onBack={onTeamBack}
                  onFixtureClick={onFixtureClick}
                  onTeamClick={onTeamClick}
                />
              ) : (
                <div className="team-loading">
                  <div className="team-loading-spinner" />
                </div>
              )}
            </ErrorBoundary>
          </div>
        ) : activePage === 'match' && selected ? (
          <div className="container match-container">
            <ErrorBoundary name="Match Page">
              <MatchPage
                fixture={selected}
                builder={builder}
                teams={teams}
                seasonStats={seasonStats}
                headToHead={headToHead}
                players={players}
                h2hStats={h2hStats}
                h2hFilter={h2hFilter}
                onH2HFilterChange={onH2HFilterChange}
                cardsContext={cardsContext}
                onBack={onMatchBack}
                onRefereeClick={handleRefereeClick}
                onTeamClick={onTeamClick}
              />
            </ErrorBoundary>
          </div>
        ) : (
          <div className={`container ${isModalOpen ? 'panel-open' : ''}`}>
            {err && (
              <div className="card">
                <div className="card-inner">
                  <div className="h1">Backend not ready</div>
                  <div className="small">{err}</div>
                  <div className="small" style={{ marginTop: 8 }}>
                    Start backend with Docker compose, run migrations, then ingest fixtures.
                  </div>
                </div>
              </div>
            )}

            <div className={`grid fixtures ${isModalOpen ? 'with-panel' : ''}`}>
              {/* Left: fixtures with inline builders on mobile */}
              <div className="grid fixtures-column" style={{ gridTemplateColumns: '1fr' }}>
                {/* Live fixtures */}
                {live.length > 0 && (
                  <CollapsibleSection
                    title="Live"
                    subtitle="In play now"
                    count={live.length}
                    defaultExpanded={true}
                    variant="live"
                  >
                    {renderFixtures(live)}
                  </CollapsibleSection>
                )}

                {/* Upcoming fixtures */}
                {upcoming.length > 0 && (
                  <CollapsibleSection
                    title="Upcoming"
                    subtitle="Tap a fixture to open Builder"
                    count={upcoming.length}
                    defaultExpanded={true}
                    variant="upcoming"
                  >
                    {renderFixtures(upcoming)}
                  </CollapsibleSection>
                )}

                {/* Completed fixtures */}
                {completed.length > 0 && (
                  <CollapsibleSection
                    title="Completed"
                    subtitle="Full time"
                    count={completed.length}
                    defaultExpanded={false}
                    variant="completed"
                  >
                    {renderFixtures(completed)}
                  </CollapsibleSection>
                )}

                {fixtures.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-text">No fixtures available</div>
                  </div>
                )}
              </div>

              {/* Middle: details (desktop only) */}
              <div className="grid detail-column" style={{ gridTemplateColumns: '1fr', alignContent: 'start' }}>
                <div className="card">
                  <div className="card-inner">
                    <div className="section-title">
                      <div>
                        <div className="h1">
                          {fixtureLabel || 'Select a fixture'}
                        </div>
                        <div className="h2">{selected ? fmtKickoff(selected.kickoff_utc) : ''}</div>
                      </div>
                      <span className="pill">{selected?.venue ?? '—'}</span>
                    </div>

                    <TabNav activeTab={tab} onTabChange={setTab} />

                    <ErrorBoundary name="Fixture Tab Content" compact>
                      {tab === 'builder' && <BuilderTab builder={builder} fixtureId={selected?.id ?? 0} fixtureLabel={fixtureLabel} homeTeamId={selected?.home_team.id} awayTeamId={selected?.away_team.id} />}
                      {tab === 'teams' && <TeamsTab teams={teams} seasonStats={seasonStats} headToHead={headToHead} fixtureId={selected?.id} />}
                      {tab === 'players' && <PlayersTab players={players} />}
                      {tab === 'h2h' && <H2HTab stats={h2hStats} teams={teams} filter={h2hFilter} onFilterChange={onH2HFilterChange} />}
                      {tab === 'cards' && <CardsTab teams={teams} cardsContext={cardsContext} />}
                      {tab === 'ai' && <InsightsSummary fixtureId={selected?.id ?? 0} />}
                    </ErrorBoundary>

                    {selected && <FixtureNotes fixtureId={selected.id} />}
                  </div>
                </div>
              </div>

              {/* Right: bet builder panel (desktop only) */}
              <BetBuilderPanel />
            </div>
          </div>
        )}

        {/* Bottom nav for mobile */}
        <BottomNav activePage={activePage} onPageChange={onPageChange} />
        <BetBuilderModal />
        <SettingsModal />
      </main>
    </div>
  )
}

// Valid page IDs for URL hash navigation
const VALID_PAGES: PageId[] = ['dashboard', 'fixtures', 'mybets', 'roadmap', 'valuebets', 'livetips', 'admin', 'referees', 'aipredictions', 'cardshub', 'goalshub', 'playerstats', 'strategies', 'dixoncoles', 'match', 'league']

// Parse hash to extract page, fixture ID, team ID, and league ID
function parseHash(hash: string): { page: PageId; fixtureId: number | null; teamId: number | null; leagueId: number | null } {
  // Default to dashboard as the landing page
  if (!hash) return { page: 'dashboard', fixtureId: null, teamId: null, leagueId: null }

  // Check for league page with league ID: #league/123
  if (hash.startsWith('league/')) {
    const leagueId = parseInt(hash.split('/')[1], 10)
    if (!isNaN(leagueId)) {
      return { page: 'league', fixtureId: null, teamId: null, leagueId }
    }
    return { page: 'dashboard', fixtureId: null, teamId: null, leagueId: null }
  }

  // Check for team page with team ID: #team/123
  if (hash.startsWith('team/')) {
    const teamId = parseInt(hash.split('/')[1], 10)
    if (!isNaN(teamId)) {
      return { page: 'match', fixtureId: null, teamId, leagueId: null }
    }
    return { page: 'dashboard', fixtureId: null, teamId: null, leagueId: null }
  }

  // Check for match page with fixture ID: #match/123
  if (hash.startsWith('match/')) {
    const fixtureId = parseInt(hash.split('/')[1], 10)
    if (!isNaN(fixtureId)) {
      return { page: 'match', fixtureId, teamId: null, leagueId: null }
    }
    return { page: 'dashboard', fixtureId: null, teamId: null, leagueId: null }
  }

  // Standard page hash
  if (VALID_PAGES.includes(hash as PageId)) {
    return { page: hash as PageId, fixtureId: null, teamId: null, leagueId: null }
  }

  return { page: 'dashboard', fixtureId: null, teamId: null, leagueId: null }
}

// Get initial page from URL hash
function getInitialPage(): PageId {
  const hash = window.location.hash.slice(1) // Remove the '#'
  return parseHash(hash).page
}

// Get initial fixture ID from URL hash (for match pages)
function getInitialMatchFixtureId(): number | null {
  const hash = window.location.hash.slice(1)
  return parseHash(hash).fixtureId
}

// Get initial team ID from URL hash (for team pages)
function getInitialTeamId(): number | null {
  const hash = window.location.hash.slice(1)
  return parseHash(hash).teamId
}

// Get initial league ID from URL hash (for league pages)
function getInitialLeagueId(): number | null {
  const hash = window.location.hash.slice(1)
  return parseHash(hash).leagueId
}

function AppWithLeague() {
  const { currentLeague, setLeague, leagues } = useLeague()
  const { isNavigating } = useNavigation()
  const [selected, setSelected] = useState<FixtureLite | null>(null)
  const [tab, setTab] = useState<Tab>('builder')
  const [activePage, setActivePage] = useState<PageId>(getInitialPage)
  const [matchFixtureId, setMatchFixtureId] = useState<number | null>(getInitialMatchFixtureId)
  const [h2hFilter, setH2hFilter] = useState<H2HFilter>('last5')
  const [selectedTeam, setSelectedTeam] = useState<TeamLite | null>(null)
  // Track team ID from URL for lazy loading (initialized from URL hash on page load)
  const [pendingTeamId, setPendingTeamId] = useState<number | null>(getInitialTeamId)
  // Track league ID for league page navigation
  const [selectedLeagueId, setSelectedLeagueId] = useState<number | null>(getInitialLeagueId)
  // Track navigation history for proper back navigation
  const previousPageRef = useRef<PageId>('dashboard')
  // Ref to track current active page for use in event handlers
  const activePageRef = useRef<PageId>(activePage)

  // Navigate to match page
  const navigateToMatch = useCallback((fixture: FixtureLite) => {
    // Remember where we came from for back navigation
    previousPageRef.current = activePageRef.current
    setSelected(fixture)
    setMatchFixtureId(fixture.id)
    setSelectedTeam(null) // Clear team page when navigating to fixture
    setActivePage('match')
    window.history.pushState(null, '', `#match/${fixture.id}`)
  }, [])

  // Navigate back from match page
  const navigateBackFromMatch = useCallback(() => {
    setMatchFixtureId(null)
    // Navigate back to where we came from, not hardcoded fixtures page
    const backPage = previousPageRef.current
    setActivePage(backPage)
    // Update URL hash appropriately
    if (backPage === 'dashboard' || backPage === 'fixtures') {
      window.history.pushState(null, '', backPage === 'dashboard' ? '#dashboard' : window.location.pathname)
    } else {
      window.history.pushState(null, '', `#${backPage}`)
    }
  }, [])

  // Navigate to team page - accepts full TeamLite object
  const handleTeamClick = useCallback((team: TeamLite) => {
    // Remember where we came from for back navigation (only if not already on match/team page)
    if (activePageRef.current !== 'match') {
      previousPageRef.current = activePageRef.current
    }
    setSelectedTeam(team)
    setActivePage('match')
    setMatchFixtureId(null) // Clear fixture to prevent showing fixture page briefly
    window.history.pushState(null, '', `#team/${team.id}`)
  }, [])

  // Navigate back from team page
  const handleTeamBack = useCallback(() => {
    setSelectedTeam(null)
    // Go back to match page if we came from a fixture (selected is set)
    if (selected) {
      setActivePage('match')
      window.history.pushState(null, '', `#match/${selected.id}`)
    } else {
      // Otherwise go back to where we came from (dashboard, fixtures, etc.)
      const backPage = previousPageRef.current
      setActivePage(backPage)
      if (backPage === 'dashboard') {
        window.history.pushState(null, '', '#dashboard')
      } else if (backPage === 'fixtures') {
        window.history.pushState(null, '', window.location.pathname)
      } else {
        window.history.pushState(null, '', `#${backPage}`)
      }
    }
  }, [selected])

  // Navigate to league page
  const handleLeagueClick = useCallback((apiLeagueId: number) => {
    // Remember where we came from for back navigation
    if (activePageRef.current !== 'league') {
      previousPageRef.current = activePageRef.current
    }
    setSelectedLeagueId(apiLeagueId)
    setActivePage('league')
    window.history.pushState(null, '', `#league/${apiLeagueId}`)
  }, [])

  // Navigate back from league page
  const handleLeagueBack = useCallback(() => {
    setSelectedLeagueId(null)
    const backPage = previousPageRef.current
    setActivePage(backPage)
    if (backPage === 'dashboard') {
      window.history.pushState(null, '', '#dashboard')
    } else if (backPage === 'fixtures') {
      window.history.pushState(null, '', window.location.pathname)
    } else {
      window.history.pushState(null, '', `#${backPage}`)
    }
  }, [])

  // Keep activePageRef in sync with activePage state
  useEffect(() => {
    activePageRef.current = activePage
  }, [activePage])

  // Sync URL hash with active page
  useEffect(() => {
    // Skip for match pages - they handle their own hash
    if (activePage === 'match') return

    // Update URL hash when page changes
    const newHash = activePage === 'fixtures' ? '' : `#${activePage}`
    if (window.location.hash !== newHash && window.location.hash !== `#${activePage}`) {
      window.history.replaceState(null, '', newHash || window.location.pathname)
    }
  }, [activePage])

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1)

      // Check for league page URL: #league/123
      if (hash.startsWith('league/')) {
        const leagueId = parseInt(hash.split('/')[1], 10)
        if (!isNaN(leagueId)) {
          // Track where we came from for back navigation
          if (activePageRef.current !== 'league') {
            previousPageRef.current = activePageRef.current
          }
          setSelectedLeagueId(leagueId)
          setActivePage('league')
          return
        }
      }

      // Check for team page URL: #team/123
      if (hash.startsWith('team/')) {
        const teamId = parseInt(hash.split('/')[1], 10)
        if (!isNaN(teamId)) {
          // Track where we came from for back navigation (only if not already on match/team page)
          if (activePageRef.current !== 'match') {
            previousPageRef.current = activePageRef.current
          }
          // If we already have this team loaded, just ensure we're on the team page
          if (selectedTeam?.id === teamId) {
            setActivePage('match')
            return
          }
          // Otherwise, set pending team ID to trigger fetch
          // Clear selected fixture so back button goes to previous page, not fixture
          setSelected(null)
          setMatchFixtureId(null)
          setPendingTeamId(teamId)
          setActivePage('match')
          return
        }
      }

      // Check for match page URL: #match/123
      if (hash.startsWith('match/')) {
        const fixtureId = parseInt(hash.split('/')[1], 10)
        if (!isNaN(fixtureId)) {
          // Track where we came from for back navigation (only if not already on match page)
          if (activePageRef.current !== 'match') {
            previousPageRef.current = activePageRef.current
          }
          // Clear selected fixture if navigating to a different fixture
          // This ensures the fixture resolution effect will run
          if (selected?.id !== fixtureId) {
            setSelected(null)
          }
          setActivePage('match')
          setMatchFixtureId(fixtureId)
          setSelectedTeam(null) // Clear team selection when going to match
          setPendingTeamId(null)
          return
        }
      }

      // Standard page navigation
      const { page, fixtureId, leagueId } = parseHash(hash)
      setActivePage(page)
      setMatchFixtureId(fixtureId)
      setSelectedTeam(null) // Clear team selection on other navigation
      setPendingTeamId(null)
      setSelectedLeagueId(leagueId) // Set league ID if present
    }
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [selectedTeam, selected])

  // Fetch team data when navigating via URL hash (#team/{id})
  useEffect(() => {
    if (!pendingTeamId) return

    // Fetch minimal team data to populate selectedTeam
    apiGet<{ team: TeamLite }>(`/teams/${pendingTeamId}`)
      .then((data) => {
        if (data?.team) {
          setSelectedTeam(data.team)
          setPendingTeamId(null)
        }
      })
      .catch((err) => {
        console.error('Failed to fetch team:', err)
        setPendingTeamId(null)
        // Navigate back to fixtures on error
        setActivePage('fixtures')
        window.history.replaceState(null, '', window.location.pathname)
      })
  }, [pendingTeamId])

  // Track selected fixture ID to preserve selection across polls
  const selectedIdRef = useRef<number | null>(null)
  // Track if we've done the initial selection
  const hasInitialSelectionRef = useRef(false)

  // Track fixtures page usage
  useEffect(() => {
    if (activePage === 'fixtures') {
      const cleanup = trackFeatureEnter(FeatureNames.FIXTURES)
      return cleanup
    }
  }, [activePage])

  // Check if we're in Live Today mode (cross-league view)
  const isLiveToday = isLiveTodayMode(currentLeague)

  // SWR hooks for data fetching with automatic caching, deduplication, and focus refetch
  // In Live Today mode, pass undefined to fetch all leagues with days=1
  const {
    data: fixturesData,
    error: fixturesError,
    mutate: mutateFixtures,
  } = useFixtures(isLiveToday ? undefined : currentLeague.api_league_id, isLiveToday ? 1 : 10)

  // Check if we need to fetch a fixture directly (for dashboard navigation)
  // Only fetch if we have a matchFixtureId, we're on match page, and fixture isn't selected
  const needsDirectFetch = activePage === 'match' && matchFixtureId && !selected
  const { data: directFetchedFixture } = useFixtureLite(needsDirectFetch ? matchFixtureId : null)

  // Fixture detail hooks - only fetch when a fixture is selected
  const { data: builder } = useFixtureBuilder(selected?.id ?? null)
  const { data: teams } = useFixtureTeams(selected?.id ?? null)
  const { data: seasonStats } = useFixtureSeasonStats(selected?.id ?? null)
  const { data: headToHead } = useFixtureHeadToHead(selected?.id ?? null)
  const { data: players } = useFixturePlayers(selected?.id ?? null)
  const { data: h2hStats } = useH2HStats(selected?.id ?? null, h2hFilter)
  const { data: cardsContext } = useFixtureCardsContext(selected?.id ?? null)

  // Memoize fixtures array to prevent unnecessary rerenders
  // In Live Today mode, sort by league first, then by kickoff time
  const fixtures = useMemo(() => {
    const data = fixturesData ?? []
    if (isLiveToday && data.length > 0) {
      // Sort by league_id first, then by kickoff time
      return [...data].sort((a, b) => {
        // First sort by league_id (nulls last)
        const leagueA = a.league_id ?? 9999
        const leagueB = b.league_id ?? 9999
        if (leagueA !== leagueB) return leagueA - leagueB
        // Then by kickoff time
        return new Date(a.kickoff_utc).getTime() - new Date(b.kickoff_utc).getTime()
      })
    }
    return data
  }, [fixturesData, isLiveToday])

  // Track which league's fixtures we last auto-selected for
  const lastAutoSelectLeagueRef = useRef<number | null>(null)
  // Track the league we navigated to - used to prevent reset when navigation completes
  const navigatedToLeagueRef = useRef<number | null>(null)

  // Handle navigation from SearchModal - switches league and selects fixture
  const handleLeagueSwitch = useCallback(
    (leagueApiId: number) => {
      const targetLeague = leagues.find((l) => l.api_league_id === leagueApiId)
      if (targetLeague) {
        setLeague(targetLeague)
      }
    },
    [leagues, setLeague]
  )

  // When navigation completes, mark this league as "already selected for" to prevent auto-select
  const handleNavigationComplete = useCallback(
    (leagueApiId: number) => {
      lastAutoSelectLeagueRef.current = leagueApiId
      hasInitialSelectionRef.current = true
      navigatedToLeagueRef.current = leagueApiId
    },
    []
  )

  // Listen for navigation requests from SearchModal
  // Navigation handler now directly sets the fixture from search data (no need to find it in fixtures list)
  useNavigationHandler(setSelected, setActivePage, handleLeagueSwitch, handleNavigationComplete)

  // Update ref when selection changes
  useEffect(() => {
    selectedIdRef.current = selected?.id ?? null
  }, [selected])

  // Clear navigatedToLeagueRef when navigation completes
  useEffect(() => {
    if (isNavigating) {
      // Will be set by handleNavigationComplete
    } else {
      // Clear after navigation is fully done (isNavigating went false)
      // Use a small delay to ensure the reset effect doesn't fire first
      const timer = setTimeout(() => {
        navigatedToLeagueRef.current = null
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isNavigating])

  // Reset selection when league changes (but not if navigating to a specific fixture)
  useEffect(() => {
    if (isNavigating) return // Don't reset if we're navigating to a fixture
    // Don't reset if this is the league we just navigated to
    if (navigatedToLeagueRef.current === currentLeague.api_league_id) return
    setSelected(null)
    setH2hFilter('last5')
    hasInitialSelectionRef.current = false
  }, [currentLeague.api_league_id, isNavigating])

  // Auto-select first fixture after fixtures load for the current league
  useEffect(() => {
    // Skip auto-select if we're navigating to a specific fixture
    if (isNavigating) return
    // Skip auto-select if we have a specific fixture ID we're trying to load
    if (matchFixtureId) return

    // Only auto-select if:
    // 1. We have fixtures
    // 2. Nothing is currently selected
    // 3. We haven't auto-selected for this league yet
    if (
      fixtures.length > 0 &&
      !selected &&
      lastAutoSelectLeagueRef.current !== currentLeague.api_league_id
    ) {
      // Use setTimeout to let React finish current render cycle
      const timer = setTimeout(() => {
        const firstLiveFixture = fixtures.find((fixture) => fixture.status === 'LIVE')
        setSelected(firstLiveFixture ?? fixtures[0])
        lastAutoSelectLeagueRef.current = currentLeague.api_league_id
        hasInitialSelectionRef.current = true
      }, 50) // Small delay to ensure new fixtures have loaded
      return () => clearTimeout(timer)
    }
  }, [fixtures, selected, currentLeague.api_league_id, isNavigating, matchFixtureId])

  // When navigating to match page via URL (e.g., #match/123), find and select the fixture
  // First try to find in current fixtures, then use directly fetched fixture
  useEffect(() => {
    if (activePage === 'match' && matchFixtureId && !selected) {
      // First check if fixture is in current fixtures list
      const fixtureInList = fixtures.find((f: FixtureLite) => f.id === matchFixtureId)
      if (fixtureInList) {
        setSelected(fixtureInList)
      } else if (directFetchedFixture) {
        // Use directly fetched fixture (from different league)
        setSelected(directFetchedFixture)
      }
    }
  }, [activePage, matchFixtureId, selected, fixtures, directFetchedFixture])

  // Handle live updates from WebSocket (Phase 12a)
  // Updates the SWR cache directly for real-time score updates
  const handleLiveUpdate = useCallback((update: LiveUpdate) => {
    if (update.type === 'fixture_update') {
      // Update fixture in SWR cache
      mutateFixtures(
        (prev) =>
          prev?.map((f) =>
            String(f.id) === update.fixture_id
              ? {
                  ...f,
                  home_score: update.data.home_score ?? f.home_score,
                  away_score: update.data.away_score ?? f.away_score,
                  status: update.data.status ?? f.status,
                  status_detail: update.data.status_detail ?? f.status_detail,
                  elapsed: update.data.elapsed ?? f.elapsed,
                }
              : f
          ),
        { revalidate: false } // Don't refetch, just update cache
      )
      // Also update selected fixture if it matches
      setSelected((prev) =>
        prev && String(prev.id) === update.fixture_id
          ? {
              ...prev,
              home_score: update.data.home_score ?? prev.home_score,
              away_score: update.data.away_score ?? prev.away_score,
              status: update.data.status ?? prev.status,
              status_detail: update.data.status_detail ?? prev.status_detail,
              elapsed: update.data.elapsed ?? prev.elapsed,
            }
          : prev
      )
    } else if (update.type === 'stats_update') {
      // Update live stats in SWR cache
      mutateFixtures(
        (prev) =>
          prev?.map((f) =>
            String(f.id) === update.fixture_id
              ? {
                  ...f,
                  home_stats: f.home_stats
                    ? {
                        ...f.home_stats,
                        corners: update.data.home_corners ?? f.home_stats.corners,
                        shots: update.data.home_shots ?? f.home_stats.shots,
                        shots_on_target: update.data.home_shots_on_target ?? f.home_stats.shots_on_target,
                        possession: update.data.home_possession ?? f.home_stats.possession,
                        fouls: update.data.home_fouls ?? f.home_stats.fouls,
                        yellows: update.data.home_yellows ?? f.home_stats.yellows,
                      }
                    : f.home_stats,
                  away_stats: f.away_stats
                    ? {
                        ...f.away_stats,
                        corners: update.data.away_corners ?? f.away_stats.corners,
                        shots: update.data.away_shots ?? f.away_stats.shots,
                        shots_on_target: update.data.away_shots_on_target ?? f.away_stats.shots_on_target,
                        possession: update.data.away_possession ?? f.away_stats.possession,
                        fouls: update.data.away_fouls ?? f.away_stats.fouls,
                        yellows: update.data.away_yellows ?? f.away_stats.yellows,
                      }
                    : f.away_stats,
                }
              : f
          ),
        { revalidate: false }
      )
    }
  }, [mutateFixtures])

  // Connect to WebSocket for live updates
  useLiveUpdates({
    onUpdate: handleLiveUpdate,
    enabled: true,
  })

  // Connect to alerts WebSocket for real-time notifications (Phase 6)
  useAlerts({ enabled: true })

  const fixtureLabel = selected
    ? `${selected.home_team.name} vs ${selected.away_team.name}`
    : ''

  const err = fixturesError ? String(fixturesError) : null

  // Refresh state for user-controlled refresh
  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    try {
      await mutateFixtures()
    } finally {
      setIsRefreshing(false)
    }
  }, [mutateFixtures])

  return (
    <MainContent
      fixtures={fixtures}
      selected={selected}
      setSelected={setSelected}
      tab={tab}
      setTab={setTab}
      builder={builder ?? null}
      teams={teams ?? null}
      seasonStats={seasonStats ?? null}
      headToHead={headToHead ?? null}
      players={players ?? null}
      h2hStats={h2hStats ?? null}
      h2hFilter={h2hFilter}
      onH2HFilterChange={setH2hFilter}
      cardsContext={cardsContext ?? null}
      err={err}
      fixtureLabel={fixtureLabel}
      activePage={activePage}
      onPageChange={setActivePage}
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
      isLiveToday={isLiveToday}
      onFixtureClick={navigateToMatch}
      onMatchBack={navigateBackFromMatch}
      matchFixtureId={matchFixtureId}
      selectedTeam={selectedTeam}
      pendingTeamId={pendingTeamId}
      onTeamClick={handleTeamClick}
      onTeamBack={handleTeamBack}
      selectedLeagueId={selectedLeagueId}
      onLeagueClick={handleLeagueClick}
      onLeagueBack={handleLeagueBack}
    />
  )
}

// Wrapper to set up auth before BetTrackerProvider mounts
function AuthSetupWrapper({ children }: { children: React.ReactNode }) {
  useAuthSetup()
  // Sync authenticated user ID to analytics for activity tracking
  useAnalyticsUserSync()
  return <>{children}</>
}

export default function App() {
  return (
    <ErrorBoundary name="Application Root">
      <SWRConfig value={swrConfig}>
        <ThemeProvider>
          <LeagueProvider>
            <OddsProvider>
              <LiveTipsProvider>
                <SidebarProvider>
                  <AuthSetupWrapper>
                    {/* Unified BetProvider replaces BetBuilderProvider + BetTrackerProvider */}
                    <BetProvider>
                      {/* Unified UserDataProvider replaces PlayerShortlistProvider + FixtureNotesProvider */}
                      <UserDataProvider>
                        {/* NavigationProvider enables SearchModal to navigate to fixtures */}
                        <NavigationProvider>
                          {/* Skip link for keyboard accessibility */}
                          <a href="#main-content" className="skip-link">
                            Skip to main content
                          </a>
                          {/* Global search modal (Cmd+K / Ctrl+K) */}
                          <SearchModal />
                          <AppWithLeague />
                          <PWAInstallBanner />
                          <Toaster
                            position="top-right"
                            containerStyle={{ top: 72 }}
                            toastOptions={{
                              className: 'statscout-toast',
                            }}
                          />
                        </NavigationProvider>
                      </UserDataProvider>
                    </BetProvider>
                  </AuthSetupWrapper>
                </SidebarProvider>
              </LiveTipsProvider>
            </OddsProvider>
          </LeagueProvider>
        </ThemeProvider>
      </SWRConfig>
    </ErrorBoundary>
  )
}
